import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import { api, WHATSAPP_LINK } from "../lib/api";
import { toast } from "sonner";

const SERVICIOS = [
  "Figura 3D Pokémon",
  "Figura 3D Dragón",
  "Juguete articulado",
  "Figura anime",
  "Llavero / Merch",
  "Libro para colorear personalizado",
  "Agenda kawaii personalizada",
  "Stickers personalizados",
  "Vinilos adhesivos",
  "Otro",
];

const Cotizar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const preServicio = location.state?.servicio || "";
  const [form, setForm] = useState({ nombre: "", telefono: "", correo: "", servicio: preServicio, descripcion: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.telefono || !form.correo || !form.servicio || !form.descripcion) {
      toast.error("Por favor completa todos los campos"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Solicitud enviada");
    } catch {
      toast.error("Error al enviar. Intenta de nuevo.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="pt-12 pb-24 min-h-screen bg-cream flex items-center" data-testid="cotizar-success">
        <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex h-20 w-20 items-center justify-center bg-yolk border-2 border-black mb-8"><CheckCircle2 className="h-10 w-10 text-black" /></div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-brand-ink mb-6">¡Solicitud recibida!</h1>
          <p className="text-gray-600 leading-relaxed mb-8">Te contactaremos en breve. Para una atención más rápida, escríbenos por WhatsApp.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WHATSAPP_LINK(`Hola, soy ${form.nombre}, acabo de enviar una solicitud de cotización para ${form.servicio}`)} target="_blank" rel="noreferrer" data-testid="cotizar-success-wa" className="btn-chunky inline-flex items-center justify-center gap-2 bg-yolk text-black px-6 py-4 text-sm">
              <MessageCircle className="h-4 w-4" /> Escribir por WhatsApp
            </a>
            <button onClick={() => navigate("/")} data-testid="cotizar-success-home" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-6 py-4 text-sm">Volver al inicio</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cotizar-page" className="pt-12 pb-24 min-h-screen bg-cream">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Cotizar ─</span>
          <h1 className="mt-3 font-heading text-5xl md:text-6xl font-extrabold tracking-tight text-brand-ink leading-tight">
            Cuéntanos tu<br /><span className="font-accent italic text-mint-deep">idea</span>.
          </h1>
          <p className="mt-5 text-gray-700 leading-relaxed">Completa el formulario y te enviaremos un presupuesto sin compromiso. O escríbenos directamente por WhatsApp.</p>
          <a href={WHATSAPP_LINK("Hola, quiero cotizar mi proyecto personalizado")} target="_blank" rel="noreferrer" data-testid="cotizar-wa-cta" className="btn-chunky inline-flex items-center gap-2 bg-yolk text-black px-6 py-3 mt-6 text-sm">
            <MessageCircle className="h-4 w-4" /> COTIZA TU PROYECTO PERSONALIZADO
          </a>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 bg-white border-2 border-black p-6 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre y apellido" name="nombre" value={form.nombre} onChange={onChange} testid="cotizar-nombre" />
            <Field label="Teléfono" name="telefono" value={form.telefono} onChange={onChange} testid="cotizar-telefono" />
          </div>
          <Field label="Correo" name="correo" type="email" value={form.correo} onChange={onChange} testid="cotizar-correo" />
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Servicio</label>
            <select name="servicio" value={form.servicio} onChange={onChange} data-testid="cotizar-servicio" className="w-full border-2 border-black px-4 py-3 text-sm bg-white focus:outline-none focus:border-mint-deep">
              <option value="">Selecciona un servicio</option>
              {SERVICIOS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Descripción</label>
            <textarea name="descripcion" value={form.descripcion} onChange={onChange} rows={5} data-testid="cotizar-descripcion" placeholder="Cuéntanos cantidades, colores, fechas, personajes y cualquier detalle relevante." className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep resize-none" />
          </div>
          <button type="submit" disabled={submitting} data-testid="cotizar-submit" className="btn-chunky w-full inline-flex items-center justify-center gap-2 bg-mint-deep text-white py-3.5 text-sm">
            <Send className="h-4 w-4" /> {submitting ? "Enviando..." : "ENVIAR SOLICITUD"}
          </button>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} data-testid={testid} className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep" />
  </div>
);

export default Cotizar;
